import type { Match } from "navigo";
import type { ChildDom } from "vanjs-core";
import { reactive } from "vanjs-ext";
import { getPrices } from "../api/prices";
import { locale } from "../locale";
import type { Route, RouteContext } from "../router";
import { state } from "../state";
import { userPlaceholder } from "../user/user";
import { centeredBox } from "../van/centered-box";
import { button, div, h1, p } from "../van/van-tags";

export class PurchaseSmsPage implements Route {
	async #confirmPurchase(): Promise<void> {
		const req = this.#ctx.api.post(`/users/${this.#reactive.user.Id}/sms`);
		req.setSignedInUserAuth();

		const res = await this.#ctx.loadingScreen.waitFor(req.do());
		if (res.ok) {
			const user = await res.json();
			state.setUser(user);
			this.#ctx.router.jump("/account");
		}
	}

	async refresh(_: Match): Promise<void> {
		this.#reactive.user = await state.getActiveUser();

		const { Sms } = await getPrices();
		this.#reactive.amount =
			this.#reactive.user.Country === "SE" ? Sms.Sek : Sms.Eur;
		this.#reactive.currency =
			this.#reactive.user.Country === "SE" ? "SEK" : "EUR";
	}

	#ctx: RouteContext;
	#reactive = reactive({
		amount: 0,
		currency: "",
		user: userPlaceholder,
	});

	ChildDom: ChildDom;

	constructor(ctx: RouteContext) {
		this.#ctx = ctx;
		this.ChildDom = centeredBox(
			{ size: "narrow" },
			h1(
				{ class: "title" },
				locale.string({
					enGB: "Confirm SMS purchase",
					deDE: "SMS-Kauf bestätigen",
					frFR: "Confirmer l'achat de SMS",
					svSE: "Bekräfta köp av SMS",
				}),
			),
			div(
				{ class: "field" },
				p(() =>
					locale.string({
						enGB: `By clicking confirm you agree to the purchase and immediate delivery of 100 SMS for receiving notifications. The amount to be paid is ${this.#reactive.amount} ${this.#reactive.currency} ex VAT and will be included on your next invoice.`,
						deDE: `Durch Klicken auf Bestätigen stimmen Sie dem Kauf und der sofortigen Lieferung von 100 SMS für den Empfang von Benachrichtigungen zu. Der zu zahlende Betrag beträgt ${this.#reactive.amount} ${this.#reactive.currency} zzgl. MwSt. und wird auf Ihrer nächsten Rechnung ausgewiesen.`,
						frFR: `En cliquant sur confirmer, vous acceptez l'achat et la livraison immédiate de 100 SMS pour la réception de notifications. Le montant à payer est de ${this.#reactive.amount} ${this.#reactive.currency} HT et sera inclus sur votre prochaine facture.`,
						svSE: `Genom att klicka på bekräfta godkänner du köpet och omedelbar leverans av 100 SMS för aviseringar. Beloppet som ska betalas är ${this.#reactive.amount} ${this.#reactive.currency} exkl. moms och kommer att debiteras på din nästa faktura.`,
					}),
				),
			),
			div(
				{
					class: "field is-grouped is-multiline",
				},
				div(
					{ class: "control" },
					button(
						{
							class: "button is-primary",
							onclick: () => this.#confirmPurchase(),
						},
						locale.string({
							enGB: "Confirm",
							deDE: "Bestätigen",
							frFR: "Confirmer",
							svSE: "Bekräfta",
						}),
					),
				),
				div(
					{
						class: "control",
						onclick: () => {
							this.#ctx.router.jump("/account");
						},
					},
					button(
						{ class: "button" },
						locale.string({
							enGB: "Back",
							deDE: "Zurück",
							frFR: "Retour",
							svSE: "Tillbaka",
						}),
					),
				),
			),
		);
	}
}
